function unwrap(result) {
  for (let depth = 0; depth < 5; depth++) {
    if (typeof result === 'string') { result = JSON.parse(result); continue; }
    if (Number(result?.statusCode) >= 400 || result?.error) throw new Error(result?.error || 'Archived QA could not prepare the workbook.');
    if (result?.response !== undefined) { result = result.response; continue; }
    if (result?.body !== undefined) { result = result.body; continue; }
    return result;
  }
  throw new Error('Archived QA returned an invalid workbook response.');
}

export async function pollArchivedQaJob(invoke, body, { maxAttempts = 420, pollIntervalMs = 2000,
  wait = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds)), onProgress,
  validateOutputs = outputs => typeof outputs?.jsonPath === 'string' && typeof outputs?.workbookPath === 'string' } = {}) {
  let job = unwrap(await invoke('instanceHandler', { body }));
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    onProgress?.({ jobId: job.jobId, documentId: body.document_id, phase: job.phase, progress: job.progress });
    if (job.status === 'Succeeded') {
      if (!validateOutputs(job.outputs)) throw new Error('The prepared QA workbook is unavailable.');
      return job;
    }
    if (!/^[a-f0-9]{64}$/.test(job?.jobId || '')) throw new Error('Archived QA did not accept the workbook request.');
    if (job.status === 'Failed') throw new Error(job.message || 'The QA workbook could not be generated. Your edits were not published.');
    if (!['Queued', 'Processing'].includes(job.status)) throw new Error('The QA workbook returned an invalid processing state.');
    if (attempt === maxAttempts - 1) break;
    await wait(pollIntervalMs);
    job = unwrap(await invoke('instanceHandler', { body: { target: 'get-archived-qa-workbook-status',
      archivedQa: body.archivedQa, access_token: body.access_token, jobId: job.jobId } }));
  }
  throw new Error('QA workbook preparation is still pending. Your edits were not published and remain in the tool.');
}

export async function requestQaWorkbook(invoke, body, options) {
  const job = await pollArchivedQaJob(invoke, body, options);
  return { success: true, ...job.outputs, jobId: job.jobId };
}

export async function saveQaWorkbookWithBridge(fullPath, jsonFilePath, sheetNames, document = {}, inputFileType, { authenticate, invoke, logError }) {
    try {
        const user = await authenticate({
            bypassCache: true  // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
        });
        const body = {
            "target": "extractDocument",
            "access_token": user.signInUserSession.idToken.jwtToken,
            "fullPath": fullPath,
            "jsonFilePath": jsonFilePath,
            "option": "qa_process",
            ...(document?.archivedQa ? { archivedQa: document.archivedQa } : {}),
            "sheetNames": sheetNames,
            "inputFileType": inputFileType
        };
        if (document?.document_id) {
            body.document_id = document.document_id;
        }
        if (document?.documentFullPath) {
            body.documentFullPath = document.documentFullPath;
        }
        if (document?.documentName) {
            body.documentName = document.documentName;
        }
        if (document?.templateName) {
            body.templateName = document.templateName;
        }
        const init = {
            body: JSON.stringify(body)
        };
        if (document?.archivedQa) {
            if (!body.sheetNames?.length) delete body.sheetNames;
            return requestQaWorkbook(invoke, body);
        }
        await invoke("instanceHandler", init);
    } catch (err) {
        logError(err);
        return false;
    }
    return true;
}
